import { z } from 'zod';

import type { Utils } from '@cadstrom/sigma-engine-shared';

const PROTOCOL = 'cad-renderer-sync';
const PROTOCOL_VERSION = 1;
const DEFAULT_HEARTBEAT_MS = 5000;
const DEFAULT_STALE_PEER_MS = 15000;

export type CadRendererSyncScope = {
	projectId: string;
	revisionId: string;
};

const CadRendererSyncScopeSchema = z.object({
	projectId: z.string(),
	revisionId: z.string(),
}) satisfies Utils.Zot<CadRendererSyncScope>;

export enum CadRendererSyncNamespace {
	Presence = 'presence',
	InteractionState = 'interactionstate',
	Layers = 'layers',
	Grid = 'grid',
	ViewHydration = 'view-hydration',
}

const CadRendererSyncNamespaceSchema = z.nativeEnum(
	CadRendererSyncNamespace,
) satisfies Utils.Zot<CadRendererSyncNamespace>;

export enum CadRendererPresenceEvent {
	Hello = 'hello',
	Ack = 'ack',
	Heartbeat = 'heartbeat',
	Goodbye = 'goodbye',
}

export enum CadRendererSnapshotEvent {
	Snapshot = 'snapshot',
	SnapshotRequest = 'snapshotRequest',
	SnapshotResponse = 'snapshotResponse',
}

export enum CadRendererInteractionStateEvent {
	SelectElementsByProperty = 'selectElementsByProperty',
	ClearSelection = 'clearSelection',
	HighlightElementsByProperty = 'highlightElementsByProperty',
	ClearHighlight = 'clearHighlight',
}

export type CadRendererPublishEvent = CadRendererSnapshotEvent | CadRendererInteractionStateEvent;
export type CadRendererSyncEvent = CadRendererPresenceEvent | CadRendererPublishEvent;

const CadRendererSyncEventSchema = z.union([
	z.nativeEnum(CadRendererPresenceEvent),
	z.nativeEnum(CadRendererSnapshotEvent),
	z.nativeEnum(CadRendererInteractionStateEvent),
]) satisfies Utils.Zot<CadRendererSyncEvent>;

export type CadRendererSyncEnvelope<
	TPayload extends object = any,
	TEvent extends CadRendererSyncEvent = CadRendererSyncEvent,
> = {
	protocol: 'cad-renderer-sync';
	protocolVersion: 1;
	scope: CadRendererSyncScope;
	sourceWindowId: string;
	namespace: CadRendererSyncNamespace;
	event: TEvent;
	key: string;
	sentAt: number;
	clock: number;
	payload: TPayload;
};

const CadRendererSyncEnvelopeSchema = z.object({
	protocol: z.literal(PROTOCOL),
	protocolVersion: z.literal(PROTOCOL_VERSION),
	scope: CadRendererSyncScopeSchema,
	sourceWindowId: z.string(),
	namespace: CadRendererSyncNamespaceSchema,
	event: CadRendererSyncEventSchema,
	key: z.string(),
	sentAt: z.number(),
	clock: z.number(),
	payload: z.object({}).passthrough(),
}) satisfies Utils.Zot<CadRendererSyncEnvelope>;

export type CadRendererSyncStatus = {
	isConfigured: boolean;
	isSupported: boolean;
	isEnabled: boolean;
	peerCount: number;
	enabledPeerCount: number;
	hasPeers: boolean;
	isLinked: boolean;
	setEnabled: (enabled: boolean) => void;
};

export type CadRendererBroadcastManagerOptions = {
	channelFactory?: (name: string) => BroadcastChannelLike;
	now?: () => number;
	windowId?: string;
	heartbeatMs?: number;
	stalePeerMs?: number;
};

export type BroadcastChannelLike = {
	postMessage: (message: unknown) => void;
	close: () => void;
	addEventListener?: (type: 'message', listener: (event: MessageEvent<unknown>) => void) => void;
	removeEventListener?: (type: 'message', listener: (event: MessageEvent<unknown>) => void) => void;
	onmessage?: ((event: MessageEvent<unknown>) => void) | null;
};

type PeerState = {
	enabled: boolean;
	lastSeen: number;
};

type PresencePayload = {
	enabled: boolean;
};

const PresencePayloadSchema = z.object({
	enabled: z.boolean(),
}) satisfies Utils.Zot<PresencePayload>;

export class CadRendererBroadcastManager {
	public readonly channelName: string;
	public readonly windowId: string;
	public readonly isSupported: boolean;

	private readonly channel: BroadcastChannelLike | null;
	private readonly now: () => number;
	private readonly heartbeatMs: number;
	private readonly stalePeerMs: number;
	private readonly peers = new Map<string, PeerState>();
	private readonly envelopeListeners = new Set<(envelope: CadRendererSyncEnvelope) => void>();
	private readonly statusListeners = new Set<(status: CadRendererSyncStatus) => void>();
	private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
	private stalePeerTimer: ReturnType<typeof setInterval> | null = null;
	private enabled = true;
	private closed = false;
	private clock = 0;

	public constructor(
		public readonly scope: CadRendererSyncScope,
		options: CadRendererBroadcastManagerOptions = {},
	) {
		this.now = options.now ?? (() => Date.now());
		this.heartbeatMs = options.heartbeatMs ?? DEFAULT_HEARTBEAT_MS;
		this.stalePeerMs = options.stalePeerMs ?? DEFAULT_STALE_PEER_MS;
		this.windowId = options.windowId ?? createWindowId();
		this.channelName = createCadRendererSyncChannelName(scope);

		const factory =
			options.channelFactory ??
			(typeof BroadcastChannel === 'function' ? (name: string) => new BroadcastChannel(name) : undefined);
		// Some environments expose BroadcastChannel but throw on construction (e.g. restricted
		// iframes or privacy modes); fall back to a disabled manager rather than crashing sync init.
		try {
			this.channel = factory?.(this.channelName) ?? null;
		} catch (error) {
			console.warn('CadRendererBroadcastManager: BroadcastChannel construction failed; sync disabled', error);
			this.channel = null;
		}
		this.isSupported = this.channel !== null;

		if (this.channel) {
			this.addMessageListener();
			this.publishPresence(CadRendererPresenceEvent.Hello);
			this.heartbeatTimer = setInterval(
				() => this.publishPresence(CadRendererPresenceEvent.Heartbeat),
				this.heartbeatMs,
			);
			this.stalePeerTimer = setInterval(() => this.removeStalePeers(), Math.min(this.heartbeatMs, this.stalePeerMs));
		}
	}

	public getStatus(): CadRendererSyncStatus {
		const peerCount = this.peers.size;
		const enabledPeerCount = Array.from(this.peers.values()).filter((peer) => peer.enabled).length;
		return {
			isConfigured: true,
			isSupported: this.isSupported,
			isEnabled: this.enabled,
			peerCount,
			enabledPeerCount,
			hasPeers: peerCount > 0,
			isLinked: this.isSupported && this.enabled && enabledPeerCount > 0,
			setEnabled: (enabled) => this.setEnabled(enabled),
		};
	}

	public setEnabled(enabled: boolean): void {
		if (this.enabled === enabled) {
			return;
		}

		this.enabled = enabled;
		this.publishPresence(CadRendererPresenceEvent.Heartbeat);
		this.emitStatus();
	}

	public subscribe(listener: (envelope: CadRendererSyncEnvelope) => void): () => void {
		this.envelopeListeners.add(listener);
		return () => {
			this.envelopeListeners.delete(listener);
		};
	}

	public subscribeStatus(listener: (status: CadRendererSyncStatus) => void): () => void {
		this.statusListeners.add(listener);
		listener(this.getStatus());
		return () => {
			this.statusListeners.delete(listener);
		};
	}

	public publish<TPayload extends object>(
		namespace: Exclude<CadRendererSyncNamespace, CadRendererSyncNamespace.Presence>,
		event: CadRendererPublishEvent,
		payload: TPayload,
	): void {
		if (!this.channel || this.closed || !this.enabled) {
			return;
		}

		this.post(namespace, event, payload);
	}

	public close(): void {
		if (this.closed) {
			return;
		}

		this.publishPresence(CadRendererPresenceEvent.Goodbye);
		this.closed = true;

		if (this.heartbeatTimer !== null) {
			clearInterval(this.heartbeatTimer);
			this.heartbeatTimer = null;
		}
		if (this.stalePeerTimer !== null) {
			clearInterval(this.stalePeerTimer);
			this.stalePeerTimer = null;
		}

		this.removeMessageListener();
		this.channel?.close();
		this.peers.clear();
		this.envelopeListeners.clear();
		this.statusListeners.clear();
	}

	private publishPresence(event: CadRendererPresenceEvent): void {
		if (!this.channel || this.closed) {
			return;
		}
		this.post<PresencePayload>(CadRendererSyncNamespace.Presence, event, { enabled: this.enabled });
	}

	private post<TPayload extends object>(
		namespace: CadRendererSyncNamespace,
		event: CadRendererSyncEvent,
		payload: TPayload,
	): void {
		if (!this.channel || this.closed) {
			return;
		}

		this.clock += 1;
		const envelope: CadRendererSyncEnvelope<TPayload> = {
			protocol: PROTOCOL,
			protocolVersion: PROTOCOL_VERSION,
			scope: this.scope,
			sourceWindowId: this.windowId,
			namespace,
			event,
			key: `${this.scope.revisionId}:${namespace}:${event}`,
			sentAt: this.now(),
			clock: this.clock,
			payload,
		};
		this.channel.postMessage(envelope);
	}

	private addMessageListener(): void {
		if (!this.channel) {
			return;
		}
		if (this.channel.addEventListener) {
			this.channel.addEventListener('message', this.handleMessage);
		} else {
			this.channel.onmessage = this.handleMessage;
		}
	}

	private removeMessageListener(): void {
		if (!this.channel) {
			return;
		}
		if (this.channel.removeEventListener) {
			this.channel.removeEventListener('message', this.handleMessage);
		} else if (this.channel.onmessage === this.handleMessage) {
			this.channel.onmessage = null;
		}
	}

	private readonly handleMessage = (event: MessageEvent<unknown>) => {
		const envelope = parseEnvelope(event.data);
		if (!envelope || !this.isEnvelopeForThisManager(envelope)) {
			return;
		}

		if (envelope.namespace === CadRendererSyncNamespace.Presence) {
			this.handlePresence(envelope);
			return;
		}

		if (!this.enabled) {
			return;
		}

		for (const listener of this.envelopeListeners) {
			listener(envelope);
		}
	};

	private isEnvelopeForThisManager(envelope: CadRendererSyncEnvelope): boolean {
		return (
			envelope.sourceWindowId !== this.windowId &&
			envelope.scope.projectId === this.scope.projectId &&
			envelope.scope.revisionId === this.scope.revisionId
		);
	}

	private handlePresence(envelope: CadRendererSyncEnvelope): void {
		const payload = parsePresencePayload(envelope.payload);
		if (!payload) {
			return;
		}

		if (envelope.event === CadRendererPresenceEvent.Goodbye) {
			const removed = this.peers.delete(envelope.sourceWindowId);
			if (removed) {
				this.emitStatus();
			}
			return;
		}

		const previous = this.peers.get(envelope.sourceWindowId);
		this.peers.set(envelope.sourceWindowId, {
			enabled: payload.enabled,
			lastSeen: this.now(),
		});

		if (envelope.event === CadRendererPresenceEvent.Hello) {
			this.publishPresence(CadRendererPresenceEvent.Ack);
		}

		if (!previous || previous.enabled !== payload.enabled) {
			this.emitStatus();
		}
	}

	private removeStalePeers(): void {
		const cutoff = this.now() - this.stalePeerMs;
		let changed = false;
		for (const [windowId, peer] of this.peers) {
			if (peer.lastSeen < cutoff) {
				this.peers.delete(windowId);
				changed = true;
			}
		}
		if (changed) {
			this.emitStatus();
		}
	}

	private emitStatus(): void {
		const status = this.getStatus();
		for (const listener of this.statusListeners) {
			listener(status);
		}
	}
}

export function createCadRendererSyncChannelName(scope: CadRendererSyncScope): string {
	return `cadstrom:cad-renderer-sync:v1:${scope.projectId}:${scope.revisionId}`;
}

export function createDisabledCadRendererSyncStatus(isConfigured = false): CadRendererSyncStatus {
	return {
		isConfigured,
		isSupported: false,
		isEnabled: false,
		peerCount: 0,
		enabledPeerCount: 0,
		hasPeers: false,
		isLinked: false,
		setEnabled: () => undefined,
	};
}

function parseEnvelope(value: unknown): CadRendererSyncEnvelope | null {
	const result = CadRendererSyncEnvelopeSchema.safeParse(value);
	return result.success ? result.data : null;
}

function parsePresencePayload(value: unknown): PresencePayload | null {
	const result = PresencePayloadSchema.safeParse(value);
	return result.success ? result.data : null;
}

function createWindowId(): string {
	if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
		return crypto.randomUUID();
	}
	return `window-${Math.random().toString(36).slice(2)}`;
}
